import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useActiveClinic } from "../../../context/ClinicContext"

function ActionButton({ onClick, children, primary = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: primary ? "none" : "1px solid #cbd5e1",
        background: primary ? "#2563eb" : "#ffffff",
        color: primary ? "#ffffff" : "#0f172a",
        borderRadius: 10,
        padding: "10px 14px",
        fontWeight: 700,
        cursor: "pointer"
      }}
    >
      {children}
    </button>
  )
}

function ChecklistItem({ done, title, description, action }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "28px minmax(0, 1fr) auto",
        gap: 12,
        alignItems: "start",
        padding: "12px 0",
        borderTop: "1px solid #e5e7eb"
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          fontSize: 13,
          fontWeight: 800,
          background: done ? "#dcfce7" : "#eff6ff",
          color: done ? "#166534" : "#1d4ed8",
          border: `1px solid ${done ? "#bbf7d0" : "#bfdbfe"}`
        }}
      >
        {done ? "✓" : "•"}
      </div>

      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#0f172a",
            marginBottom: 4
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#64748b",
            lineHeight: 1.45
          }}
        >
          {description}
        </div>
      </div>

      {action || <div />}
    </div>
  )
}

export default function OnboardingWelcomeCard() {
  const navigate = useNavigate()
  const { initContext, clinicOptions, activeClinicId } = useActiveClinic()

  const onboarding = initContext?.onboarding
  const account = initContext?.account
  const user = initContext?.user

  // 🔥 estado de cierre persistente
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem("onboarding_dismissed")
    if (stored === "true") {
      setDismissed(true)
    }
  }, [])

  if (!onboarding || !account || !user || dismissed) return null

  const clinicsCount = (clinicOptions || []).filter((item) => item.id !== "ALL").length

  const isOwner =
    account?.membershipRole === "OWNER" ||
    account?.membershipRole === "ADMIN"

  if (!isOwner) return null

  // 🔥 condición mejorada
  const showCard =
    onboarding.hasClinics &&
    (
      clinicsCount <= 1 ||
      !onboarding.hasTeam ||
      !onboarding.hasDoctors ||
      !onboarding.hasTreatments
    )

  if (!showCard) return null

  const clinicName =
    clinicOptions.find((item) => item.id === activeClinicId)?.name ||
    "tu consultorio"

  return (
    <div
      style={{
        margin: "16px 20px 0",
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 18,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "start"
        }}
      >
        <div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 6
            }}
          >
            Tu consultorio ya está listo
          </div>

          <div
            style={{
              fontSize: 14,
              color: "#64748b",
              lineHeight: 1.5,
              maxWidth: 720
            }}
          >
            {user.name}, ya tienes una base funcional para empezar a trabajar en{" "}
            <strong>{clinicName}</strong>. No necesitas una configuración rígida
            para arrancar; solo te recomendamos estos pasos para dejarlo más fino.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap"
          }}
        >
          <ActionButton primary onClick={() => navigate("/agenda")}>
            Ir a agenda
          </ActionButton>

          <ActionButton onClick={() => navigate("/clinics")}>
            Ver clínicas
          </ActionButton>

          {/* 🔥 botón cerrar */}
          <button
            onClick={() => {
              localStorage.setItem("onboarding_dismissed", "true")
              setDismissed(true)
            }}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 16,
              cursor: "pointer",
              color: "#64748b"
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <ChecklistItem
          done={clinicsCount > 0}
          title="Consultorio base creado"
          description="Ya generamos tu clínica principal para que puedas empezar a usar el sistema desde este momento."
          action={<ActionButton onClick={() => navigate("/clinics")}>Revisar</ActionButton>}
        />

        <ChecklistItem
          done={onboarding.hasChairs}
          title="Sillón inicial listo"
          description="Ya existe un sillón base para operar agenda y capacidad. Más adelante puedes renombrarlo o agregar más."
          action={<ActionButton onClick={() => navigate("/chairs")}>Abrir sillas</ActionButton>}
        />

        <ChecklistItem
          done={onboarding.hasDoctors}
          title="Define tu operación clínica"
          description="Si tú mismo atenderás pacientes o si agregarás doctores, aquí puedes dejar tu estructura médica lista."
          action={<ActionButton onClick={() => navigate("/doctors")}>Abrir doctores</ActionButton>}
        />

        <ChecklistItem
          done={onboarding.hasTeam}
          title="Agrega tu equipo"
          description="Invita asistentes, recepción o usuarios de apoyo solo si realmente los necesitas. El sistema no te obliga a hacerlo para arrancar."
          action={<ActionButton onClick={() => navigate("/users")}>Abrir usuarios</ActionButton>}
        />

        {/* 🔥 NUEVO BLOQUE */}
        <ChecklistItem
          done={onboarding.hasTreatments}
          title="Catálogo de tratamientos"
          description="Ya puedes trabajar con los tratamientos base o personalizar tu catálogo según tu clínica."
          action={
            <ActionButton onClick={() => navigate("/treatment-catalog")}>
              Abrir catálogo
            </ActionButton>
          }
        />

        <ChecklistItem
          done={false}
          title="Personaliza tu clínica"
          description="Renombra tu consultorio, ajusta detalles operativos y deja la base lista a tu manera."
          action={<ActionButton onClick={() => navigate("/clinics")}>Personalizar</ActionButton>}
        />
      </div>
    </div>
  )
}